import type { TFunction } from "i18next";

import { confidenceLabel } from "@/components/agents/functional/aderencia/aderenciaData";

import type { ScopeItemDetailRow } from "../data/scopeItems";

/**
 * Exportação em Excel do detalhe "Scope items ativos" (EF-FUNC-004 RF-07, #1344 S5).
 *
 * Sai o que a tabela mostra, com os filtros aplicados, nas colunas do protótipo:
 * código e nome em colunas separadas, para a planilha filtrar por código. É um
 * arquivo simples, sem proteção, diferente da planilha de revisão do Catálogo,
 * que volta para o myGROW pela carga.
 *
 * A guarda contra fórmula é gravar tudo como texto: o SheetJS escreve string
 * como célula `s`, que o Excel abre como texto mesmo começando com `=`. O
 * apóstrofo da guarda do CSV (`scopeActivationFilter.ts`) não serve aqui: no
 * `.xlsx` ele ficaria escrito dentro da célula.
 */

/** Larguras do protótipo, em caracteres; a observação é a mais larga. */
const COLUMN_WIDTHS = [10, 48, 26, 10, 11, 11, 80];

export function scopeItemsMatrix(rows: ScopeItemDetailRow[], t: TFunction): string[][] {
  const header = [
    t("Code"),
    t("Scope Item"),
    t("LoB"),
    t("Decision"),
    t("Confidence"),
    t("Clean Core"),
    t("Curation note"),
  ];
  const body = rows.map((row) => {
    return [
      row.scopeItemId,
      row.name ?? "",
      row.lob ?? "",
      // A tabela só tem ativos; a coluna existe porque a EF a desenha.
      t("Active"),
      row.confidence ? t(confidenceLabel(row.confidence)) : "",
      row.cleanCore ? t("Yes") : "",
      row.note ?? "",
    ];
  });
  return [header, ...body];
}

/**
 * `myGROW_Diagnostic_ScopeItems_<projeto>_AAAAMMDD_HHMM.xlsx`, com o prefixo fixo
 * em qualquer idioma e a hora local de quem baixou, como o carimbo do protótipo.
 * O que não é letra nem número no nome do projeto vira `_` (um "AL5 [09/10/2026]"
 * traria `/`); sem nome, a parte sai.
 */
export function scopeItemsFileName(projectName: string | undefined, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const project = (projectName ?? "").replace(/[^\p{L}\p{N}-]+/gu, "_").replace(/^_+|_+$/g, "");
  return ["myGROW_Diagnostic_ScopeItems", project, stamp].filter(Boolean).join("_") + ".xlsx";
}

/** Baixa a planilha; o SheetJS só carrega no clique. */
export async function downloadScopeItemsWorkbook(
  rows: ScopeItemDetailRow[],
  t: TFunction,
  projectName: string | undefined,
): Promise<void> {
  const XLSX = await import("xlsx");
  const sheet = XLSX.utils.aoa_to_sheet(scopeItemsMatrix(rows, t));
  sheet["!cols"] = COLUMN_WIDTHS.map((wch) => ({ wch }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, t("Active scope items"));
  XLSX.writeFile(workbook, scopeItemsFileName(projectName, new Date()));
}
